import { z } from 'zod';
import { CONVERSATION_PRIORITIES, type ConversationPriority } from '../conversations/contract';
import type { ReasonOnResolve } from '../conversations/distribution-contract';
import type { CustomFieldValues } from '../custom-fields/contract';

/**
 * The service cases contract (docs/specs/046-atendimento-motivo-protocolo-e-backfill/spec.md):
 * the shapes the API answers, the zod of what it accepts and the limits both sides share.
 * Mirrors `apps/api/src/modules/cases/domain`.
 */

export const CASE_STATUSES = ['open', 'pending', 'resolved', 'canceled'] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

export const CASE_ACTIVE_STATUSES: readonly CaseStatus[] = ['open', 'pending'];

export const CASE_SOURCES = ['conversation', 'manual', 'automation'] as const;
export type CaseSource = (typeof CASE_SOURCES)[number];

export const CASE_CONVERSATION_ROLES = ['origin', 'linked'] as const;
export type CaseConversationRole = (typeof CASE_CONVERSATION_ROLES)[number];

export {
  DEFAULT_PROTOCOL_TEXT,
  PROTOCOL_MESSAGE,
  REASON_ON_RESOLVE,
  type ProtocolMessage,
  type ReasonOnResolve,
} from '../conversations/distribution-contract';

export const CASE_LIMITS = {
  subjectMax: 160,
  descriptionMax: 4000,
  reasonLabelMin: 2,
  reasonLabelMax: 60,
  reasonDepth: 2,
  reasonsMax: 200,
  protocolTextMax: 500,
  listPageSize: 50,
  listMaxPageSize: 100,
  previousShown: 5,
} as const;

export const CASE_PERMISSIONS = {
  view: 'core.case.view',
  create: 'core.case.create',
  update: 'core.case.update',
  resolve: 'core.case.resolve',
  cancel: 'core.case.cancel',
  manageReasons: 'core.service-reason.manage',
} as const;

export const CASE_ERROR_CODES = [
  'CASE_NOT_FOUND',
  'CASE_INVALID',
  'CASE_STATUS_CONFLICT',
  'SERVICE_REASON_REQUIRED',
  'SERVICE_REASON_INVALID',
  'SERVICE_REASON_NOT_FOUND',
  'SERVICE_REASON_LABEL_TAKEN',
] as const;

/* ------------------------------------------------------------------ shapes */

export interface PersonRef {
  membershipId: string;
  name: string;
}

export interface ServiceReason {
  id: string;
  /** Null: a first-level reason. */
  parentId: string | null;
  label: string;
  active: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface CaseConversation {
  id: string;
  role: CaseConversationRole;
  linkedAt: string;
  status: string;
  channelType: string;
  connection: { id: string; name: string };
  lastMessageAt: string;
}

export interface ServiceCase {
  id: string;
  number: number;
  /** `AAAA-000123`. */
  protocol: string;
  status: CaseStatus;
  source: CaseSource;
  contact: { id: string; name: string } | null;
  customer: { id: string; displayName: string } | null;
  unit: { id: string; name: string };
  queue: { id: string; name: string } | null;
  team: { id: string; name: string } | null;
  assignee: PersonRef | null;
  priority: ConversationPriority;
  reason: { id: string; label: string; parent: { id: string; label: string } | null } | null;
  outcome: { id: string; label: string } | null;
  subject: string | null;
  description: string | null;
  customFields: CustomFieldValues;
  openedAt: string;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  resolvedBy: PersonRef | null;
  canceledAt: string | null;
  reopenedFrom: { id: string; protocol: string } | null;
  /** Spec 047: the satisfaction survey of the case (absent on an older API). */
  satisfaction?: {
    status: 'sent' | 'answered' | 'expired';
    score: number | null;
    comment: string | null;
    sentAt: string;
    answeredAt: string | null;
  } | null;
  /** The origin conversation first, then the linked ones. */
  conversations: CaseConversation[];
  createdAt: string;
  updatedAt: string;
}

export interface CaseListPage {
  items: ServiceCase[];
  nextCursor: string | null;
}

/** What the conversation panel reads: the case, the previous ones, the open ones to link to. */
export interface ConversationCaseView {
  current: ServiceCase | null;
  previous: ServiceCase[];
  openOfParty: ServiceCase[];
}

/* ------------------------------------------------------------------ schemas */

const id = z.string().min(1);
const optionalId = id.nullable().optional();
const subject = z.string().trim().max(CASE_LIMITS.subjectMax).nullable().optional();
const description = z.string().trim().max(CASE_LIMITS.descriptionMax).nullable().optional();
const customFieldValue = z.union([
  z.string().max(4000),
  z.number(),
  z.boolean(),
  z.array(z.string().max(120)).max(50),
  z.null(),
]);
const customFields = z.record(z.string().max(60), customFieldValue).optional();

export const createCaseSchema = z
  .object({
    contactId: optionalId,
    customerId: optionalId,
    subject,
    description,
    unitId: optionalId,
    queueId: optionalId,
    assigneeMembershipId: optionalId,
    priority: z.enum(CONVERSATION_PRIORITIES).optional(),
    reasonId: optionalId,
    customFields,
    conversationId: optionalId,
  })
  .strict()
  .refine((body) => Boolean(body.contactId || body.customerId), {
    message: 'contactId or customerId is required',
    path: ['contactId'],
  });
export type CreateCaseInput = z.input<typeof createCaseSchema>;

export const updateCaseSchema = z
  .object({
    subject,
    description,
    queueId: optionalId,
    assigneeMembershipId: optionalId,
    priority: z.enum(CONVERSATION_PRIORITIES).optional(),
    reasonId: optionalId,
    customFields,
  })
  .strict();
export type UpdateCaseInput = z.input<typeof updateCaseSchema>;

export const resolveCaseSchema = z.object({ outcomeId: optionalId, reasonId: optionalId }).strict();
export type ResolveCaseInput = z.input<typeof resolveCaseSchema>;

export const cancelCaseSchema = z
  .object({ note: z.string().trim().max(CASE_LIMITS.descriptionMax).nullable().optional() })
  .strict();
export type CancelCaseInput = z.input<typeof cancelCaseSchema>;

export interface ListCasesQuery {
  status?: CaseStatus[];
  queueId?: string;
  assigneeMembershipId?: string;
  reasonId?: string;
  contactId?: string;
  customerId?: string;
  from?: string;
  to?: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

export const serviceReasonInputSchema = z
  .object({
    label: z.string().trim().min(CASE_LIMITS.reasonLabelMin).max(CASE_LIMITS.reasonLabelMax),
    parentId: optionalId,
    active: z.boolean().optional(),
  })
  .strict();
export type ServiceReasonInput = z.input<typeof serviceReasonInputSchema>;

export const updateServiceReasonSchema = z
  .object({
    label: z.string().trim().min(CASE_LIMITS.reasonLabelMin).max(CASE_LIMITS.reasonLabelMax),
    active: z.boolean(),
  })
  .partial()
  .strict();
export type UpdateServiceReasonInput = z.input<typeof updateServiceReasonSchema>;

/* ------------------------------------------------------------------ rules */

/** The protocol a contact quotes: the year the case opened, a dash and six digits. */
export function protocolOf(openedAt: string, number: number): string {
  return `${new Date(openedAt).getUTCFullYear()}-${String(number).padStart(6, '0')}`;
}

/** Parents by order, each followed by its children by order. */
export function reasonTree(
  reasons: readonly ServiceReason[],
): { parent: ServiceReason; children: ServiceReason[] }[] {
  const byOrder = (a: ServiceReason, b: ServiceReason) => a.order - b.order;
  return reasons
    .filter((reason) => reason.parentId === null)
    .toSorted(byOrder)
    .map((parent) => ({
      parent,
      children: reasons.filter((reason) => reason.parentId === parent.id).toSorted(byOrder),
    }));
}

/** "Troca › Produto com defeito", or just the label of a first-level reason. */
export function reasonPath(reason: ServiceCase['reason']): string | null {
  if (!reason) return null;
  return reason.parent ? `${reason.parent.label} › ${reason.label}` : reason.label;
}

/** Whether a case is still open for work. */
export function isCaseActive(serviceCase: Pick<ServiceCase, 'status'>): boolean {
  return CASE_ACTIVE_STATUSES.includes(serviceCase.status);
}

const STRICTNESS: Readonly<Record<ReasonOnResolve, number>> = { off: 0, optional: 1, required: 2 };

/**
 * What resolving these conversations asks of the contact reason: the strictest setting of their
 * queues. A conversation without a queue asks nothing.
 */
export function reasonRuleOf(
  queues: readonly ({ reasonOnResolve: ReasonOnResolve } | null)[],
): ReasonOnResolve {
  return queues.reduce<ReasonOnResolve>(
    (rule, queue) =>
      queue && STRICTNESS[queue.reasonOnResolve] > STRICTNESS[rule] ? queue.reasonOnResolve : rule,
    'off',
  );
}
